"use client";

/**
 * AdminTrialBadge — indicateur INTERNE (admin uniquement) signalant que la
 * fiche médecin courante est en essai Gold ou Premium cette semaine ISO.
 *
 * Pourquoi pas visible pour le public : décision produit 2026-05-15 — le
 * tag "Essai" faisait fuir les patients (signal négatif "ce médecin n'est
 * pas vraiment recommandé"). Le boost reste opéré (placement remonté) mais
 * indiscernable d'un vrai abonné côté patient.
 *
 * Fix P0 2026-07-06 : ce composant était un Server Component qui appelait
 * `getAdminFromRequest()` (donc `headers()`/`cookies()`) pendant le rendu
 * RSC de /medecin/[slug] et /[specialite]/[ville]/[name]. Ces deux pages
 * sont en ISR (`revalidate=3600` + `generateStaticParams`) depuis le fix
 * perf du 25/06 (commit 1ae4491ce) — sur une page rendue cacheable, toute
 * lecture de cookies()/headers() imbriquée fait planter Next.js avec
 * `DYNAMIC_SERVER_USAGE` → HTTP 500 en prod. Comme `generateStaticParams`
 * de /medecin/[slug] renvoie une liste VIDE, 100% des requêtes runtime
 * faisaient de l'ISR on-demand → 100% crashaient dès qu'elles atteignaient
 * le render (sauf early-redirect proxy.ts).
 *
 * Fix : composant client qui fetch `/api/admin/physician-trial-badge`
 * après mount (Route Handler = toujours request-scoped, jamais ISR).
 * Zéro dynamic API dans l'arbre SSR de la page médecin — ISR intact.
 * Coût : le badge apparaît ~100-300ms après hydration au lieu d'être
 * dans le HTML initial — invisible en pratique (badge admin-only, pas
 * un élément UI patient).
 */
import { useEffect, useState } from "react";

type Props = {
  slug: string;
  tier: "gold" | "premium" | "free" | "unclaimed";
  citySlug: string;
  specialties: string[];
};

type BadgeData = {
  isAdmin: boolean;
  trial?: "gold" | "premium" | null;
  tier?: string | null;
  week?: number;
};

export function AdminTrialBadge({ slug, tier }: Props) {
  const [data, setData] = useState<BadgeData | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/admin/physician-trial-badge?slug=${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json: BadgeData | null) => {
        if (!cancelled && json) setData(json);
      })
      .catch(() => {
        // silencieux : badge admin-only, ne doit jamais perturber la page
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!data?.isAdmin) return null;

  const trial = data.trial ?? null;
  const week = data.week ?? 0;

  // Si pas en trial : badge "Statut" minimal (utile pour admin)
  if (!trial) {
    if (tier === "gold" || tier === "premium") {
      return (
        <div className="fixed right-3 top-3 z-50 rounded-lg bg-emerald-700 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white shadow-lg ring-2 ring-emerald-300">
          {tier === "gold" ? "👑 GOLD payant" : "💎 PREMIUM payant"}
        </div>
      );
    }
    return null;
  }

  const colors = trial === "gold"
    ? "bg-amber-500 ring-amber-200 text-amber-950"
    : "bg-violet-500 ring-violet-200 text-violet-950";
  const label = trial === "gold" ? "👑 Essai GOLD" : "💎 Essai PREMIUM";

  return (
    <div
      className={`fixed right-3 top-3 z-50 flex flex-col items-end gap-1 rounded-lg ${colors} px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow-lg ring-2`}
      title="Visible admin uniquement. Le médecin est en placement d'essai cette semaine ISO."
    >
      <span>{label}</span>
      <span className="text-[10px] font-medium normal-case opacity-80">
        Semaine S{week} · base tier : {tier}
      </span>
    </div>
  );
}
